import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { useExperience } from '../../contexts/ExperienceContext';
import { weekCases } from '../../data/journeys';
import { needById } from '../../data/needs';
import { brand } from '../../data/palette';
import { productById } from '../../data/products';
import { Advance } from '../ui/Advance';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
const ease = [0.16, 1, 0.3, 1] as const;

/** Recognisable situations from a normal week, each mapped to the family. */
export function WeekStage() {
  const {
    signals,
    push,
    next,
    direction
  } = useExperience();
  const [open, setOpen] = useState<string | undefined>();
  const toggle = (id: string) => {
    setOpen(open === id ? undefined : id);
    push('exploredCases', id);
  };
  return <Stage direction={direction} align="start" className="min-h-screen py-28 sm:py-32" labelledBy="week-title" data-id="element-371" data-mp="19a0sfbdwpb4b">
      <div className="max-w-2xl" data-id="element-372" data-mp="297ozv5h9p8y8">
        <Eyebrow accent={brand.aqua} data-id="element-373" data-mp="296e0dhuuuncx">Your typical week</Eyebrow>
        <StageTitle id="week-title" size="display-sm" className="mt-7" data-id="element-374" data-mp="2d87uf7ajot44">
          Want to see what Filtek could cover in your typical week?
        </StageTitle>
      </div>

      <ul className="mt-14 border-t border-enamel/10" data-id="element-375" data-mp="2i61264rhxv6i">
        {weekCases.map(item => {
        const need = needById(item.need);
        const product = productById(need.productId);
        const isOpen = open === item.id;
        const isSeen = signals.exploredCases.includes(item.id);
        return <li key={item.id} className="border-b border-enamel/10" data-id="element-376" data-mp="1zhm97bxmc732">
              <button type="button" onClick={() => toggle(item.id)} aria-expanded={isOpen} className="group flex w-full items-center gap-5 py-5 text-left sm:gap-8" data-id="element-377" data-mp="1zf9lsj93d4im">
                <span className="label-mono w-28 shrink-0 text-[9px] text-mute/70 sm:w-36" data-id="element-378" data-mp="1br00difsiis0">
                  {item.frequency}
                </span>
                <span className="flex-1 text-lg font-light leading-snug tracking-[-0.02em] transition-colors duration-500 sm:text-2xl" style={{
              color: isOpen ? need.accent : isSeen ? 'var(--enamel)' : brand.quiet
            }} data-id="element-379" data-mp="13ozdzdhocr74">
                  {item.situation}
                </span>
                <motion.span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border" animate={{
              rotate: isOpen ? 135 : 0,
              borderColor: isOpen ? need.accent : 'rgba(244,242,237,0.2)'
            }} transition={{
              duration: 0.5,
              ease
            }} data-id="element-380" data-mp="kry7fo8h2j12">
                  <PlusIcon className="h-3.5 w-3.5 text-enamel" aria-hidden="true" data-id="element-381" data-mp="ptnktaj638sb" />
                </motion.span>
              </button>

              <AnimatePresence initial={false} data-id="element-382" data-mp="2179acwsxvced">
                {isOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.6,
              ease
            }} className="overflow-hidden" data-id="element-383" data-mp="3j4cnfqddhm1u">
                    <div className="grid gap-8 pb-10 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] sm:gap-10 sm:pl-[9rem]" data-id="element-384" data-mp="3f3iz4vw0q58f">
                      {item.image && <img src={item.image} alt={item.situation} className="h-36 w-full object-cover opacity-85 sm:h-40" data-id="element-385" data-mp="ao9oqa48n7pn" />}
                      <div data-id="element-386" data-mp="1uoacrvzjsgr">
                        <p className="label-mono text-[9px]" style={{
                    color: need.accent
                  }} data-id="element-387" data-mp="1tv0hi1qcmue">
                          {need.label}
                        </p>
                        <p className="mt-3 text-base font-light leading-relaxed text-enamel" data-id="element-388" data-mp="udbplqoawd9x">
                          {item.coverage}
                        </p>
                        <p className="mt-4 text-sm font-light text-mute" data-id="element-389" data-mp="2wx0g6ixoyy5e">
                          Filtek <span className="text-enamel" data-id="element-390" data-mp="2wz3f5sgto1m9">{product.shortName}</span> ·{' '}
                          {need.technique}
                        </p>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </li>;
      })}
      </ul>

      {signals.exploredCases.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      ease
    }} className="mt-14 flex flex-wrap items-center justify-between gap-6" data-id="element-391" data-mp="3gmue7er029i2">
          <p className="label-mono text-[10px] text-mute" data-id="element-392" data-mp="85g2d82j5b23">
            {signals.exploredCases.length} situation
            {signals.exploredCases.length > 1 ? 's' : ''} explored
          </p>
          <Advance label="Continue" onClick={next} accent={brand.aqua} data-id="element-393" data-mp="apm73hyyx5jp" />
        </motion.div>}
    </Stage>;
}